<template>
  <div class="h-game-main">
    <div class="header">
      <button class="back-button" @click="$router.push('/match')">
        <img src="@/assets/images/back.svg"> Zurück
      </button>
      <h1>Spiel "H"</h1>
      <button class="info-button" @click="showRules = true" title="Regeln anzeigen">i</button>
    </div>

    <!-- Phase 0: Setup / Player Selection -->
    <div class="setup-container" v-if="phase === 0">
      <h2>Spieler wählen (max. 10)</h2>
      <p class="subtitle">Wähle die Mitspieler aus eurer Gruppe aus:</p>
      
      <div v-if="loadingUsers" class="loading-msg">Lade Gruppenmitglieder...</div>
      
      <div class="player-selection" v-if="!loadingUsers">
        <div 
          v-for="user in poolUsers" 
          :key="user.id" 
          class="player-pill"
          :class="{'selected': selectedPlayers.includes(user)}"
          @click="togglePlayer(user)"
        >
          {{ user.name || user.email }}
        </div>
      </div>

      <div class="setup-actions">
        <button 
          class="start-button" 
          :disabled="selectedPlayers.length === 0 || selectedPlayers.length > 10"
          @click="startGame"
        >
          Spiel starten ({{ selectedPlayers.length }}/10)
        </button>
      </div>
    </div>

    <!-- Phase 1, 2, 3: The actual game board -->
    <div class="game-container" v-if="phase > 0">
      <div class="board-container">
        <!-- We use a CSS grid to align the H -->
        <div class="h-board">
          <!-- Left Column -->
          <div class="vertical-col left">
             <div class="card-slot" v-for="i in 5" :key="'L'+i">
               <PlayingCard 
                 v-if="tableCards.left[i-1]" 
                 :card="tableCards.left[i-1]" 
                 :revealed="revealed.left[i-1]" 
               />
               <span class="sip-label" v-if="revealed.left[i-1]">{{i}} 🍺<br>verteilen</span>
             </div>
          </div>

          <!-- Middle Crossbar -->
          <div class="horizontal-col center">
            <div class="card-slot middle-slot" v-for="i in 2" :key="'M'+i">
               <PlayingCard 
                 v-if="tableCards.middle[i-1]" 
                 :card="tableCards.middle[i-1]" 
                 :revealed="revealed.middle[i-1]" 
               />
               <span class="sip-label" v-if="revealed.middle[i-1]">EX <br>{{i===1 ? 'verteilen' : 'selbst'}}</span>
            </div>
          </div>

          <!-- Right Column -->
          <div class="vertical-col right">
             <div class="card-slot" v-for="i in 5" :key="'R'+i">
               <PlayingCard 
                 v-if="tableCards.right[i-1]" 
                 :card="tableCards.right[i-1]" 
                 :revealed="revealed.right[i-1]" 
               />
               <span class="sip-label" v-if="revealed.right[i-1]">{{i}} 🍺<br>selbst</span>
             </div>
          </div>
        </div>
      </div>

      <!-- Action Button for Game Flow -->
      <div class="game-flow-actions">
        <!-- Display current message context -->
        <p class="flow-message" v-if="flowMessage">{{ flowMessage }}</p>
        <p class="flow-hint" v-if="waitingForConfirmation">
          💡 Hinweis: Bei mehreren passenden Karten auf der Hand multipliziert sich der Wert entsprechend!
        </p>
        
        <button v-if="waitingForConfirmation" class="confirm-btn" @click="confirmAction">
          Verstanden / Getrunken
        </button>
        <button v-else-if="!gameOver" class="next-btn" @click="nextCard">
          Nächste Karte aufdecken
        </button>
        <button v-else class="next-btn" @click="resetGame">
          Neues Spiel
        </button>
      </div>

      <!-- Player Hand View (scrollable horizontal list) -->
      <div class="player-hands-container">
        <h3>Mitspieler & Karten</h3>
        <div class="player-hands">
          <div class="player-hand" v-for="player in players" :key="player.id" :class="{'has-match': matchingPlayers.includes(player)}">
            <span class="player-name">{{ player.name || player.email }}</span>
            <div class="hand-cards">
              <PlayingCard v-for="(card, i) in player.hand" :key="i" :card="card" :revealed="true" size="small" />
            </div>
            <div class="match-indicator" v-if="matchingPlayers.includes(player)">
              🔥 MATCH ({{ player.hand.filter(c => c.value === currentMatchCard.value).length }}x)
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Rules Modal -->
    <div class="rules-modal" v-if="showRules" @click.self="showRules = false">
      <div class="rules-content">
        <h2>Regeln: Spiel "H"</h2>
        <div class="rules-text">
          <p><strong>Ablauf:</strong> Die Karten liegen verdeckt in Form eines "H". Zuerst wird die linke Spalte von oben nach unten aufgedeckt, dann die rechte Spalte, und zuletzt die Mitte.</p>
          <p><strong>Match:</strong> Wenn eine aufgedeckte Karte mit dem <strong>Wert</strong> (Farbe ist egal) einer deiner Handkarten übereinstimmt, tritt die jeweilige Regel in Kraft.</p>
          <ul>
            <li><strong>Links (Verteilen):</strong> Je nach Position der Karte 1-5 Schlücke an Mitspieler verteilen.</li>
            <li><strong>Rechts (Trinken):</strong> Je nach Position 1-5 Schlücke selbst trinken.</li>
            <li><strong>Mitte 1:</strong> 1 EX an einen beliebigen Spieler verteilen!</li>
            <li><strong>Mitte 2:</strong> Dein eigenes Getränk EXEN! Oh je...</li>
          </ul>
          <p><strong>Zusatzregel:</strong> Hast du den passenden Wert mehrfach auf der Hand, wird die Anzahl der Schlücke bzw. EX <strong>multipliziert</strong>!</p>
        </div>
        <button class="start-button close-rules-btn" @click="showRules = false">Alles klar!</button>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/AuthService.js';
import PlayingCard from '@/components/PlayingCard.vue';

export default {
  name: 'HGameView',
  components: {
    PlayingCard
  },
  data() {
    return {
      showRules: false,
      phase: 0, // 0=Setup, 1=Left, 2=Right, 3=Middle
      loadingUsers: true,
      poolUsers: [],
      selectedPlayers: [],
      
      players: [], // { id, name, hand: [card1, card2] }
      deck: [], // shuffled deck

      // The H structure on the board
      tableCards: { left: [], middle: [], right: [] },
      revealed: { left: [], middle: [], right: [] },

      // Flow tracking
      currentColumn: 'left', // 'left', 'right', 'middle'
      currentIndex: 0, // index within column

      flowMessage: "",
      waitingForConfirmation: false,
      matchingPlayers: [],
      currentMatchCard: null,

      gameOver: false
    }
  },
  computed: {
    selectedPool() {
      return this.$store.getters.getSelectedPool;
    }
  },
  async mounted() {
    if (!this.$store.getters.isLoggedIn) {
      this.$router.push('/login');
      return;
    }
    
    // Load pool users
    if (this.selectedPool && this.selectedPool.pool_id) {
      try {
        let response = await AuthService.getUsersInPool({ pool_id: this.selectedPool.pool_id });
        if (response.success) {
          this.poolUsers = response.msg;
          // Preselect self
          const myId = this.$store.getters.getUser.id;
          const me = this.poolUsers.find(u => u.id === myId);
          if (me) this.selectedPlayers.push(me);
        }
      } catch(e) {
        console.error("Error fetching users", e)
      }
    }
    this.loadingUsers = false;
  },
  methods: {
    togglePlayer(user) {
      const idx = this.selectedPlayers.findIndex(u => u.id === user.id);
      if (idx >= 0) {
        this.selectedPlayers.splice(idx, 1);
      } else {
        if (this.selectedPlayers.length < 10) {
          this.selectedPlayers.push(user);
        }
      }
    },
    
    createDeck() {
      const suits = ['heart', 'diamond', 'club', 'spade'];
      const values = ['7', '8', '9', 'J', 'Q', 'K', 'A'];
      const deck = [];
      
      for (let suit of suits) {
        for (let val of values) {
          deck.push({ suit, value: val });
        }
      }
      return this.shuffle(deck);
    },

    shuffle(array) {
      const arr = [...array];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    },

    startGame() {
      // 1. Init players
      this.players = this.selectedPlayers.map(u => ({
        id: u.id,
        name: u.name || u.email,
        hand: []
      }));

      // 2. Create Deck
      this.deck = this.createDeck();

      // 3. Deal cards to players
      for (let p of this.players) {
        p.hand.push(this.deck.pop());
        p.hand.push(this.deck.pop());
      }

      // 4. Deal board (12 cards)
      this.tableCards = { left: [], right: [], middle: [] };
      this.revealed = { left: [], right: [], middle: [] };
      
      for(let i=0; i<5; i++) {
        this.tableCards.left.push(this.deck.pop());
        this.revealed.left.push(false);
      }
      for(let i=0; i<5; i++) {
        this.tableCards.right.push(this.deck.pop());
        this.revealed.right.push(false);
      }
      for(let i=0; i<2; i++) {
        this.tableCards.middle.push(this.deck.pop());
        this.revealed.middle.push(false);
      }

      // 5. Setup Game variables
      this.phase = 1;
      this.currentColumn = 'left';
      this.currentIndex = 0;
      this.gameOver = false;
      this.flowMessage = "Das H liegt! Decke die erste Karte links auf.";
      this.waitingForConfirmation = false;
      this.matchingPlayers = [];
      this.currentMatchCard = null;
    },

    nextCard() {
      let col = this.currentColumn;
      let idx = this.currentIndex;

      if (this.gameOver) return;

      // Reveal the card
      this.revealed[col][idx] = true;
      const card = this.tableCards[col][idx];
      this.currentMatchCard = card;

      // Check for matches
      // Remember matching rule: just the value needs to match
      this.matchingPlayers = this.players.filter(p => 
        p.hand.some(c => c.value === card.value)
      );

      let matchDetails = this.matchingPlayers.map(p => {
        let count = p.hand.filter(c => c.value === card.value).length;
        return { name: p.name, count: count };
      });

      // Logic based on column
      if (this.matchingPlayers.length > 0) {
        this.waitingForConfirmation = true;

        if (col === 'left') {
          let sips = idx + 1; // index 0 = 1 sip, index 4 = 5 sips
          this.flowMessage = `Match! ` + matchDetails.map(m => `${m.name} darf ${m.count * sips} Schlücke verteilen`).join(' | ');
        } else if (col === 'right') {
          let sips = idx + 1;
          this.flowMessage = `Match! ` + matchDetails.map(m => `${m.name} muss ${m.count * sips} Schlücke trinken`).join(' | ');
        } else if (col === 'middle') {
          if (idx === 0) {
            this.flowMessage = `Match! ` + matchDetails.map(m => `${m.name} darf ${m.count} EX an einen beliebigen Spieler verteilen`).join(' | ');
          } else {
            this.flowMessage = `Match! ` + matchDetails.map(m => `${m.name} muss ${m.count}x sein eigenes Getränk EXEN`).join(' | ');
          }
        }
      } else {
        // No matches, instantly auto-proceed to next card conceptually, but let user click again or just say "no match"
        this.flowMessage = `Kein Match für ${card.value}. (Click für nächste Karte)`;
        this.prepareNextStep();
      }
    },

    confirmAction() {
      // User says they distributed / drank
      this.waitingForConfirmation = false;
      this.matchingPlayers = []; // clear highlight
      this.currentMatchCard = null;
      this.prepareNextStep();
    },

    prepareNextStep() {
      this.currentIndex++;
      
      let maxIdx = (this.currentColumn === 'middle') ? 2 : 5;

      if (this.currentIndex >= maxIdx) {
        // Switch column
        if (this.currentColumn === 'left') {
          this.currentColumn = 'right';
          this.currentIndex = 0;
          this.phase = 2;
          this.flowMessage = "Phase 2 verlangt Opfer. Rechte Seite aufdecken (Selbst trinken).";
        } else if (this.currentColumn === 'right') {
          this.currentColumn = 'middle';
          this.currentIndex = 0;
          this.phase = 3;
          this.flowMessage = "Phase 3: Der Querbalken. Zeit für die EX-Regeln.";
        } else {
          // End of game
          this.gameOver = true;
          this.flowMessage = "Das Spiel ist vorbei! Alle Karten aufgedeckt.";
        }
      } else {
        if (!this.flowMessage.includes("Kein Match") && !this.gameOver) {
           this.flowMessage = "Bereit für die nächste Karte.";
        }
      }
    },

    resetGame() {
      this.phase = 0;
      this.deck = [];
      this.tableCards = { left: [], right: [], middle: [] };
      this.revealed = { left: [], right: [], middle: [] };
      this.players = [];
      this.matchingPlayers = [];
      this.currentMatchCard = null;
      this.flowMessage = "";
      this.gameOver = false;
    }
  }
}
</script>

<style scoped>
.h-game-main {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--background);
  color: var(--primary-color);
  overflow-y: auto;
  padding-bottom: 50px;
}

.header {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid var(--border-color);
}

.header h1 {
  margin: 0;
  font-size: 1.5rem;
  margin-left: 15px;
}

.back-button {
  background: none;
  border: none;
  display: flex;
  align-items: center;
  color: var(--primary-color);
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
}
.back-button img {
  width: 24px;
  height: 24px;
  margin-right: 5px;
  filter: var(--icon-filter);
}

.setup-container {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.subtitle {
  color: var(--tertiary-color);
  margin-bottom: 20px;
}

.player-selection {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-bottom: 30px;
  max-width: 600px;
}

.player-pill {
  padding: 10px 15px;
  border-radius: 20px;
  background: var(--background-secondary);
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.player-pill.selected {
  background: var(--primary-color);
  color: var(--text-on-primary);
  border-color: var(--primary-color);
}

.start-button {
  background: var(--primary-color);
  color: var(--text-on-primary);
  padding: 15px 30px;
  border-radius: var(--radius-l);
  font-size: 1.2rem;
  font-weight: bold;
  border: none;
  cursor: pointer;
}
.start-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.game-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.board-container {
  margin-bottom: 30px;
  width: 100%;
  max-width: 600px;
  display: flex;
  justify-content: center;
}

/* H Board Layout */
.h-board {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.vertical-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.horizontal-col {
  display: flex;
  flex-direction: row;
  gap: 10px;
  /* Align it to the middle vertically */
}

.card-slot {
  width: 65px;
  height: 95px;
  background: rgba(0,0,0,0.05);
  border-radius: 8px;
  border: 1px dashed var(--border-color);
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

[data-theme='dark'] .card-slot {
  background: rgba(255,255,255,0.05);
}

.sip-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0,0,0,0.7);
  color: white;
  padding: 4px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: bold;
  text-align: center;
  pointer-events: none;
  white-space: nowrap;
}

/* Actions logic */
.game-flow-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 30px;
  width: 100%;
  max-width: 400px;
  text-align: center;
}

.flow-message {
  font-size: 1.1rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: var(--primary-color);
  min-height: 50px;
}

.flow-hint {
  font-size: 0.9rem;
  color: var(--tertiary-color);
  margin-bottom: 20px;
  font-style: italic;
}

.next-btn, .confirm-btn {
  padding: 15px 30px;
  border-radius: var(--radius-m);
  font-size: 1.1rem;
  font-weight: bold;
  border: none;
  cursor: pointer;
  width: 100%;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.next-btn {
  background: var(--background-secondary);
  color: var(--primary-color);
  border: 1px solid var(--border-color);
}

.confirm-btn {
  background: var(--primary-color);
  color: var(--text-on-primary);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

/* Players Hands */
.player-hands-container {
  width: 100%;
  max-width: 800px;
}

.player-hands-container h3 {
  margin-bottom: 15px;
  padding-left: 10px;
}

.player-hands {
  display: flex;
  overflow-x: auto;
  gap: 15px;
  padding: 10px;
  scroll-snap-type: x mandatory;
}

.player-hand {
  scroll-snap-align: start;
  flex: 0 0 160px;
  background: var(--background-secondary);
  border-radius: var(--radius-m);
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 2px solid transparent;
  position: relative;
  transition: all 0.3s;
}

.player-hand.has-match {
  border-color: #ff3b30;
  background: rgba(255, 59, 48, 0.1);
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(255, 59, 48, 0.2);
}

.player-name {
  font-weight: bold;
  margin-bottom: 10px;
  font-size: 0.95rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.hand-cards {
  display: flex;
  gap: 5px;
}

.hand-cards > * {
  transform: scale(0.8);
  transform-origin: top center;
  margin: -10px;
}

.match-indicator {
  position: absolute;
  bottom: -10px;
  background: #ff3b30;
  color: white;
  padding: 4px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: bold;
  z-index: 10;
}

/* Rules Modal */
.info-button {
  margin-left: auto;
  background: var(--background-secondary);
  color: var(--primary-color);
  border: 1px solid var(--border-color);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-weight: bold;
  font-family: serif;
  font-size: 1.1rem;
  font-style: italic;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  transition: transform 0.2s, background 0.2s;
}

.info-button:hover {
  transform: scale(1.1);
  background: var(--primary-color);
  color: var(--text-on-primary);
}

.rules-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
}

.rules-content {
  background: var(--background);
  padding: 25px;
  border-radius: var(--radius-l);
  max-width: 500px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.rules-content h2 {
  margin-top: 0;
  margin-bottom: 15px;
  color: var(--primary-color);
}

.rules-text {
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--primary-color);
}

.rules-text p {
  margin-bottom: 12px;
}

.rules-text ul {
  padding-left: 20px;
  margin-bottom: 15px;
}

.rules-text li {
  margin-bottom: 6px;
}

.close-rules-btn {
  width: 100%;
  margin-top: 15px;
}
</style>
